import { colors } from '../../theme/colors';

export const CLICK_TO_EXPAND_LABEL = 'Click to expand';

/** Gap between the pointer and the caption, so the cursor does not cover the words. */
const CURSOR_GAP_X = 12;
const CURSOR_GAP_Y = 16;

export function placeClickToExpandCaption(
  host: {
    getBoundingClientRect(): {
      left: number;
      top: number;
      width: number;
      height: number;
    };
  },
  caption: {
    offsetWidth: number;
    offsetHeight: number;
    style: { left: string; top: string; right: string; bottom: string };
  },
  clientX: number,
  clientY: number,
): void {
  const rect = host.getBoundingClientRect();
  let left = clientX - rect.left + CURSOR_GAP_X;
  let top = clientY - rect.top + CURSOR_GAP_Y;
  if (caption.offsetWidth > 0 && left + caption.offsetWidth > rect.width) {
    left = clientX - rect.left - CURSOR_GAP_X - caption.offsetWidth;
  }
  if (caption.offsetHeight > 0 && top + caption.offsetHeight > rect.height) {
    top = clientY - rect.top - CURSOR_GAP_Y - caption.offsetHeight;
  }
  caption.style.left = `${Math.round(Math.max(0, left))}px`;
  caption.style.top = `${Math.round(Math.max(0, top))}px`;
  caption.style.right = 'auto';
  caption.style.bottom = 'auto';
}

/**
 * Caption painted over a picture. `hoverScope` is the ancestor that is allowed
 * to reveal it, so an editor that does not open pictures stays quiet.
 */
export function inlineImageExpandCss(hoverScope: string): string {
  return `
${hoverScope} .ss-image-expand-host {
  position: relative;
  display: inline-block;
  max-width: 100%;
  line-height: 0;
  vertical-align: bottom;
}
${hoverScope} .ss-image-expand {
  display: none;
  position: absolute;
  z-index: 1;
  pointer-events: none;
  white-space: nowrap;
  background: ${colors.actionSolidBlack};
  color: ${colors.textLight};
  font-size: 12px;
  line-height: 16px;
  padding: 4px 8px;
}
${hoverScope} .ss-image-expand-host:hover .ss-image-expand {
  display: inline-flex;
}
`;
}

/**
 * View-mode HTML only. Pictures inside a link keep following that link, so
 * they do not grow a "click to expand" caption. `isOpenable` receives each
 * picture's ordinal among all `<img>` tags, linked ones included.
 */
export function withClickToExpandCaptions(
  html: string,
  isOpenable: (ordinal: number) => boolean = () => true,
): string {
  const skip: Array<[number, number]> = [];
  const anchorRe = /<a\b[^>]*>[\s\S]*?<\/a>/gi;
  let anchor: RegExpExecArray | null;
  while ((anchor = anchorRe.exec(html))) {
    skip.push([anchor.index, anchor.index + anchor[0].length]);
  }
  const imgRe = /<img\b[^>]*?>/gi;
  let result = '';
  let last = 0;
  let image: RegExpExecArray | null;
  let ordinal = 0;
  while ((image = imgRe.exec(html))) {
    const start = image.index;
    const insideLink = skip.some(([from, to]) => start >= from && start < to);
    const openable = !insideLink && isOpenable(ordinal);
    ordinal += 1;
    result += html.slice(last, start);
    result += !openable
      ? image[0]
      : `<span class="ss-image-expand-host">${image[0]}<span class="ss-image-expand" aria-hidden="true">${CLICK_TO_EXPAND_LABEL}</span></span>`;
    last = start + image[0].length;
  }
  return result + html.slice(last);
}
